import { Injectable } from '@angular/core';
import { DexTemplate } from '@app/modules/configuration/configuration.types';
import { TemplatesService } from '@core/services/templates.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { switchMap, tap } from 'rxjs/operators';
import {
  CloneTemplateAction,
  CreateTemplateAction,
  DeactivateTemplateAction,
  GetTemplateAction,
  ListTemplatesAction,
  ReactivateTemplateAction,
  UpdateTemplateAction,
} from './templates.actions';

const TEMPLATES_STATE_NAME = 'templates';

export interface TemplatesStateModel {
  templates: DexTemplate[];
}

@State<TemplatesStateModel>({
  name: TEMPLATES_STATE_NAME,
  defaults: {
    templates: [],
  },
})
@Injectable()
export class TemplatesState {
  constructor(private templatesService: TemplatesService) {}

  @Selector()
  static getActiveTemplates(state: TemplatesStateModel) {
    return state.templates.filter((template) => !template.deactivated);
  }

  @Selector()
  static getUnactiveTemplates(state: TemplatesStateModel) {
    return state.templates.filter((template) => !!template.deactivated);
  }

  @Selector()
  static getAllTemplates(state: TemplatesStateModel) {
    return state.templates;
  }

  @Selector()
  static getTemplateById(state: TemplatesStateModel) {
    return (templateId: string) =>
      state.templates.find((template) => template.id === templateId);
  }

  @Action(ListTemplatesAction)
  listTemplates({ patchState }: StateContext<TemplatesStateModel>) {
    return this.templatesService.listTemplates().pipe(
      tap((templates) =>
        patchState({
          templates: templates.sort((a, b) => a.name.localeCompare(b.name)),
        })
      )
    );
  }

  @Action(CreateTemplateAction)
  createTemplate(
    { dispatch }: StateContext<TemplatesStateModel>,
    { name }: CreateTemplateAction
  ) {
    return this.templatesService
      .createTemplate(name)
      .pipe(switchMap(() => dispatch(new ListTemplatesAction())));
  }

  @Action(CloneTemplateAction)
  cloneTemplate(
    { dispatch }: StateContext<TemplatesStateModel>,
    { id, name }: CloneTemplateAction
  ) {
    return this.templatesService
      .cloneTemplate(id, name)
      .pipe(switchMap(() => dispatch(new ListTemplatesAction())));
  }

  @Action(GetTemplateAction)
  getTemplate(
    { getState, patchState }: StateContext<TemplatesStateModel>,
    { id }: GetTemplateAction
  ) {
    return this.templatesService.getTemplate(id).pipe(
      tap((template) => {
        const state = getState();
        const templates = [...state.templates];
        const index = templates.findIndex((t) => t.id === id);
        if (index !== -1) {
          templates[index] = template;
        } else {
          templates.unshift(template);
        }
        patchState({ templates });
      })
    );
  }

  @Action(UpdateTemplateAction)
  updateTemplate(
    { dispatch }: StateContext<TemplatesStateModel>,
    { id, name }: UpdateTemplateAction
  ) {
    return this.templatesService
      .updateTemplate(id, name)
      .pipe(switchMap(() => dispatch(new GetTemplateAction(id))));
  }

  @Action(DeactivateTemplateAction)
  deactivateTemplate(
    { dispatch }: StateContext<TemplatesStateModel>,
    { id }: DeactivateTemplateAction
  ) {
    return this.templatesService
      .deactivateTemplate(id)
      .pipe(switchMap(() => dispatch(new GetTemplateAction(id))));
  }

  @Action(ReactivateTemplateAction)
  reactivateTemplate(
    { dispatch }: StateContext<TemplatesStateModel>,
    { id }: ReactivateTemplateAction
  ) {
    return this.templatesService
      .reactivateTemplate(id)
      .pipe(switchMap(() => dispatch(new GetTemplateAction(id))));
  }
}
